/**
 * The bill side of bridge B5, for both apps (RESERVATIONS_V2_PLAN.md §8.6 RV6-1, RV6-2; decisions 144, 148, 149): which
 * booking v1's open bill belongs to, and what its Dine-in / Customer form's submit says about that booking. Pure.
 *
 * It turns what v1's Delivery / Dine-in form holds when it is submitted (the form, the table the cashier picked, the bill's own
 * table and guest count) into the `SaleEditOfBooking` that core's `useHoldBillEdit` holds for the bill's booking until the bill saves. It decides
 * only what the edit says. Core decides what differs from the booking and what is saved (`fromSale.ts`).
 *
 * What each part says (decision 144): the table and the guest count only when they differ from the BILL's (v1's own test),
 * the contact parts only when typed and not blank. A blank phone is never sent, where v1 sent the contact on every
 * dine-in submit even when it was empty. An edit that says nothing is `undefined`, so the screen sends no request at all.
 */
import { isFilledString, isPlainRecord } from "../api/wireGuards";
import type { SaleEditContact, SaleEditOfBooking } from "./fromSale";
import type { TableRef } from "./model/types";

/** Where a bill's booking is: its id, and the branch the bill was opened on when the bill says so (decision 144). */
export interface BillBooking {
  /** The bill's `reservationId`. */
  reservationId: string;
  /** The bill's own branch; when it is absent or blank the session's branch is used. */
  branchId?: string;
}

/** The two fields of v1's open bill that say which booking it belongs to; v1's sale is untyped, so each is checked here. */
export interface BillOfBooking {
  /** The id of the booking the bill was opened from; absent for a walk-in bill. */
  reservationId?: unknown;
  /** The branch the bill was opened on; absent or blank means the session's branch. */
  branchId?: unknown;
}

/**
 * Which booking a bill belongs to: its `reservationId`, on its own branch when it names one; undefined for a bill no booking
 * holds (a walk-in), so nothing is sent.
 */
export function bookingOfBill(bill: BillOfBooking | null | undefined): BillBooking | undefined {
  if (!bill || !isFilledString(bill.reservationId)) {
    return undefined;
  }
  return isFilledString(bill.branchId) ? { reservationId: bill.reservationId, branchId: bill.branchId } : { reservationId: bill.reservationId };
}

/** The guest's name and phone as the Dine-in / Customer details form holds them; v1's form values are untyped, so each is checked here. */
export interface ContactFormValues {
  /** The first name field. */
  firstName?: unknown;
  /** The last name field. */
  lastName?: unknown;
  /** The phone number field, without a country code. */
  phoneNumber?: unknown;
}

/** What the dine-in form holds beyond the contact. */
export interface DineInFormValues extends ContactFormValues {
  /** The table's label as shown in the form; it names the table on the booking. */
  tableNumber?: unknown;
  /** The guest count field, as typed. */
  numberOfGuests?: unknown;
}

/** The table the cashier picked in the form (v1's `currentTableInfo`); it has no `resourceId` for an ad hoc table. */
export interface PickedTable {
  /** The picked table's id; absent for an ad hoc table, which no booking can hold. */
  resourceId?: string;
}

/** The table and guest count a bill's `serviceLabel` holds (the bill before an edit, or the one the server saved), as read by `billServiceLabelOf`. */
export interface BillServiceLabel {
  /** The id of the table the bill sat on. */
  resourceId?: string;
  /** The guest count the bill had. */
  count?: number;
}

/** The form's value as a whole number of guests, or `NaN` when it is not one (v1: `parseInt(data.numberOfGuests)`). */
function guestCountOfForm(value: unknown): number {
  if (typeof value === "number") {
    return value;
  }
  return typeof value === "string" ? Number.parseInt(value, 10) : Number.NaN;
}

/** The contact parts the form has typed and not left blank; undefined when it has none, so a blank form says nothing about the guest. */
function contactOfForm(form: ContactFormValues): SaleEditContact | undefined {
  const contact: SaleEditContact = {};
  if (isFilledString(form.firstName)) {
    contact.firstName = form.firstName;
  }
  if (isFilledString(form.lastName)) {
    contact.lastName = form.lastName;
  }
  if (isFilledString(form.phoneNumber)) {
    contact.phoneNumber = form.phoneNumber;
  }
  return Object.keys(contact).length === 0 ? undefined : contact;
}

/**
 * The bill's table id and guest count, read from v1's `serviceLabel`. Each field is checked here because v1 types the label
 * loosely: the phone's sale model declares it as a stub (`models/sale/index.native.ts` names only `isAdhoc`) while the bill
 * carries `resourceId` and `count` at run time. A field of the wrong kind counts as absent, which compares exactly as it did
 * unchecked: a non-string id never equals a picked table's id, and a non-number count never equals a parsed guest count.
 */
export function billServiceLabelOf(value: unknown): BillServiceLabel {
  const label: BillServiceLabel = {};
  if (!isPlainRecord(value)) {
    return label;
  }
  if (isFilledString(value.resourceId)) {
    label.resourceId = value.resourceId;
  }
  if (typeof value.count === "number") {
    label.count = value.count;
  }
  return label;
}

/** The table to move the booking to: the picked table when it is a real one (not ad hoc), is named by the form and is not the one the bill already sat on. */
function tableOfDineInSubmit(form: DineInFormValues, picked: PickedTable, billLabel: BillServiceLabel): TableRef | undefined {
  if (!isFilledString(picked.resourceId) || !isFilledString(form.tableNumber) || picked.resourceId === billLabel.resourceId) {
    return undefined;
  }
  return { id: picked.resourceId, name: form.tableNumber };
}

/** The guest count to give the booking: the form's when it is a number and is not the count the bill already had; otherwise undefined. */
function guestCountOfDineInSubmit(form: DineInFormValues, billLabel: BillServiceLabel): number | undefined {
  const guestCount = guestCountOfForm(form.numberOfGuests);
  return Number.isNaN(guestCount) || guestCount === billLabel.count ? undefined : guestCount;
}

/** The edit as an object holding only the parts that are given; undefined when none is, so there is nothing to send. */
function editOfParts(parts: SaleEditOfBooking): SaleEditOfBooking | undefined {
  const edit: SaleEditOfBooking = {};
  if (parts.tableNow !== undefined) {
    edit.tableNow = parts.tableNow;
  }
  if (parts.guestCountNow !== undefined) {
    edit.guestCountNow = parts.guestCountNow;
  }
  if (parts.contactNow !== undefined) {
    edit.contactNow = parts.contactNow;
  }
  return Object.keys(edit).length === 0 ? undefined : edit;
}

/**
 * What a dine-in submit says about the bill's booking: its table when it changed, its guest count when it changed, and the
 * contact that was typed. Undefined when it says none of these.
 */
export function saleEditOfDineInSubmit(form: DineInFormValues, picked: PickedTable, billServiceLabel: unknown): SaleEditOfBooking | undefined {
  const billLabel = billServiceLabelOf(billServiceLabel);
  return editOfParts({
    tableNow: tableOfDineInSubmit(form, picked, billLabel),
    guestCountNow: guestCountOfDineInSubmit(form, billLabel),
    contactNow: contactOfForm(form),
  });
}

/** What a customer-only submit says about the bill's booking: the contact that was typed; undefined when none was. */
export function saleEditOfCustomerSubmit(form: ContactFormValues): SaleEditOfBooking | undefined {
  return editOfParts({ contactNow: contactOfForm(form) });
}
